import React, { Fragment } from "react";
import { firstError, hasError } from "common/validation";
import Grid from "@material-ui/core/Grid";
import Categories from "file-details/components/Categories";
import MenuItem from "@material-ui/core/MenuItem";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import CustomizableSelect from "../../general-components/CustomizableSelect";
import { path } from "ramda";
import { useTranslation } from "react-i18next";
import { useLanguages } from "../use-languages";
import PublicationDate from "./PublicationDate";
import CustomizableRichTextField from "../../general-components/CustomizableRichTextField";

const BookDetailsFields = ({ errors, fields, onBlur, onFieldChange }) => {
  const { t } = useTranslation();
  const languages = useLanguages();

  return (
    <Fragment>
      <Grid container spacing={2}>
        <CustomizableTextField
          id="book-title"
          fieldOption="title"
          xs={12}
          sm={6}
          defaultLabel={t("file_details.title", "Title")}
          value={path(["title"], fields) || ""}
          onChange={onFieldChange(["title"])}
          error={hasError(errors, "title")}
          helperText={t(firstError(errors, "title"))}
          onBlur={onBlur("title", fields.title)}
          inputProps={{ maxLength: 2048, id: "book-title" }}
        />
        <CustomizableTextField
          id="book-subtitle"
          xs={12}
          sm={6}
          fieldOption="subtitle"
          defaultLabel={t("file_details.subtitle", "Subtitle (optional)")}
          value={path(["subtitle"], fields) || ""}
          onChange={onFieldChange(["subtitle"])}
          error={hasError(errors, "subtitle")}
          helperText={t(firstError(errors, "subtitle"))}
          onBlur={onBlur("subtitle", fields.subtitle)}
          inputProps={{ maxLength: 255, id: "book-subtitle" }}
        />
        <CustomizableTextField
          id="book-volume"
          fieldOption="volume"
          xs={6}
          sm={3}
          defaultLabel={t("file_details.volume", "Volume (optional)")}
          value={path(["volume"], fields) || ""}
          onChange={onFieldChange(["volume"])}
          error={hasError(errors, "volume")}
          helperText={t(firstError(errors, "volume"))}
          onBlur={onBlur("volume", fields.volume)}
          inputProps={{ maxLength: 100, id: "book-volume" }}
        />
        <CustomizableTextField
          id="book-edition"
          fieldOption="edition"
          xs={6}
          sm={3}
          defaultLabel={t("file_details.edition", "Edition (optional)")}
          value={path(["edition"], fields) || ""}
          onChange={onFieldChange(["edition"])}
          error={hasError(errors, "edition")}
          helperText={t(firstError(errors, "edition"))}
          onBlur={onBlur("edition", fields.edition)}
          inputProps={{ maxLength: 100, id: "book-edition" }}
        />
        <CustomizableTextField
          id="book-isbn"
          fieldOption="isbn"
          xs={6}
          sm={3}
          defaultLabel={t("file_details.isbn", "ISBN (optional)")}
          value={path(["isbn"], fields) || ""}
          onChange={onFieldChange(["isbn"])}
          error={hasError(errors, "isbn")}
          helperText={t(firstError(errors, "isbn"))}
          onBlur={onBlur("isbn", fields.isbn)}
          inputProps={{ maxLength: 20, id: "book-isbn" }}
        />

        <PublicationDate />

        <CustomizableRichTextField
          id="description"
          fieldOption="description"
          xs={12}
          sm={12}
          md={12}
          defaultLabel={t("file_details.description", "Description")}
          value={path(["descriptionText"], fields) || ""}
          onChange={({ text, html }) => {
            if (text === "") {
              onFieldChange(["description"])("");
              onFieldChange(["descriptionText"])("");
            } else {
              onFieldChange(["description"])(text);
              onFieldChange(["descriptionText"])(html);
            }
          }}
          error={hasError(errors, "description")}
          helperText={t(firstError(errors, "description"))}
          onBlur={onBlur("description", fields.description)}
        />

        <Grid item xs={12} sm={6}>
          <Categories
            onBlur={onBlur}
            language={
              <CustomizableSelect
                fieldOption="language"
                defaultLabel={t("file_details.language", "Language")}
                value={path(["language"], fields) || ""}
                onChange={onFieldChange(["language"])}
              >
                {languages.map(lang => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.translation}
                  </MenuItem>
                ))}
              </CustomizableSelect>
            }
          />
        </Grid>
        <CustomizableTextField
          id="book-keywords"
          fieldOption="keywords"
          xs={12}
          sm={6}
          name="keywords"
          defaultLabel={t("file_details.keywords", "Keywords (optional)")}
          value={path(["keywords"], fields) || ""}
          onChange={onFieldChange(["keywords"])}
          error={hasError(errors, "keywords")}
          helperText={
            t(firstError(errors, "keywords")) ||
            t(
              "file_details_keywords_helper_text",
              "Keywords should be separated by commas"
            )
          }
          onBlur={onBlur("keywords", fields.keywords)}
          multiline
          inputProps={{ maxLength: 1000, id: "book-keywords" }}
        />
      </Grid>
    </Fragment>
  );
};

export default BookDetailsFields;
